import { Effect, Layer, Option, pipe, Schema } from 'effect';

import {
  AUTH_REFRESH_TOKEN_MAX_AGE,
  AUTH_TOKEN_MAX_AGE,
  type CookieOptions,
  CookieKeys,
  DefaultCookieOptions,
} from '../../../config/cookies';

import { ErrorTag } from '@vs-shop/engine/data/error';
import { GetAuthTokenError, SetAuthTokenError } from '@vs-shop/engine/data/error/auth';
import { AuthToken as EngineAuthToken } from '@vs-shop/engine/service/auth/AuthToken';
import { CookiesStore } from '@vs-shop/engine/service/cookies/CookiesStore';

const make = Effect.gen(function* () {
  const cookieService = yield* CookiesStore;

  const authTokenKey = CookieKeys.AUTH_TOKEN;
  const authRefreshTokenKey = CookieKeys.AUTH_REFRESH_TOKEN;
  const authRefreshTokenExpirationKey = CookieKeys.AUTH_REFRESH_TOKEN_EXPIRATION;

  return {
    getToken: Effect.gen(function* () {
      const tokenValue = yield* cookieService.get(authTokenKey).pipe(
        Effect.catchTags({
          [ErrorTag.CookieGetError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to get auth token: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to access auth cookies: ${error.message}`,
              cause: error,
            }),
        })
      );

      return Option.fromNullishOr(tokenValue);
    }),

    setToken: (token: string) =>
      Effect.gen(function* () {
        const cookieOptions = {
          ...DefaultCookieOptions,
          maxAge: AUTH_TOKEN_MAX_AGE,
          sameSite: 'strict' as const, // Prevent CSRF attacks
        };

        yield* cookieService.set(authTokenKey, token, cookieOptions).pipe(
          Effect.catchTags({
            [ErrorTag.CookieSetError]: (error) =>
              new SetAuthTokenError({
                message: `Failed to set auth token: ${error.message}`,
                cause: error,
              }),
            [ErrorTag.CookieAccessError]: (error) =>
              new SetAuthTokenError({
                message: `Failed to access auth cookies: ${error.message}`,
                cause: error,
              }),
          })
        );

        return;
      }),

    removeToken: Effect.gen(function* () {
      const cookieOptions = {
        path: '/',
      };

      return yield* cookieService.remove(authTokenKey, cookieOptions).pipe(
        Effect.catchTags({
          [ErrorTag.CookieRemoveError]: (error) =>
            new SetAuthTokenError({
              message: `Failed to remove auth token: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new SetAuthTokenError({
              message: `Failed to access auth cookies: ${error.message}`,
              cause: error,
            }),
        })
      );
    }),

    getRefreshToken: Effect.gen(function* () {
      const tokenValue = yield* cookieService.get(authRefreshTokenKey).pipe(
        Effect.catchTags({
          [ErrorTag.CookieGetError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to get refresh token: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to access refresh token cookies: ${error.message}`,
              cause: error,
            }),
        })
      );

      return Option.fromNullishOr(tokenValue);
    }),

    getRefreshTokenExpiration: Effect.gen(function* () {
      const value = yield* cookieService.get(authRefreshTokenExpirationKey).pipe(
        Effect.catchTags({
          [ErrorTag.CookieGetError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to get refresh token expiration: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new GetAuthTokenError({
              message: `Failed to access refresh token expiration cookies: ${error.message}`,
              cause: error,
            }),
        })
      );

      return pipe(
        Option.fromNullishOr(value),
        Option.flatMap((v) => Schema.decodeOption(Schema.FiniteFromString)(v))
      );
    }),

    setTokens: (token: string, refreshToken: string, refreshTokenExpiration: number) =>
      Effect.gen(function* () {
        const authCookieOptions = {
          ...DefaultCookieOptions,
          maxAge: AUTH_TOKEN_MAX_AGE,
          sameSite: 'strict' as const,
        };

        const refreshCookieOptions = {
          ...DefaultCookieOptions,
          maxAge: AUTH_REFRESH_TOKEN_MAX_AGE,
          sameSite: 'strict' as const,
        };

        const setCookie = (key: string, value: string, cookieOptions: CookieOptions) =>
          cookieService.set(key, value, cookieOptions).pipe(
            Effect.catchTags({
              [ErrorTag.CookieSetError]: (error) =>
                new SetAuthTokenError({
                  message: `Failed to set token: ${error.message}`,
                  cause: error,
                }),
              [ErrorTag.CookieAccessError]: (error) =>
                new SetAuthTokenError({
                  message: `Failed to access cookies: ${error.message}`,
                  cause: error,
                }),
            })
          );

        yield* setCookie(authTokenKey, token, authCookieOptions);

        yield* setCookie(authRefreshTokenKey, refreshToken, refreshCookieOptions);

        yield* setCookie(
          authRefreshTokenExpirationKey,
          String(Math.round(refreshTokenExpiration)),
          refreshCookieOptions
        );
      }),

    removeAllTokens: Effect.gen(function* () {
      const cookieOptions = { path: '/' };

      yield* Effect.all([
        cookieService.remove(authTokenKey, cookieOptions),
        cookieService.remove(authRefreshTokenKey, cookieOptions),
        cookieService.remove(authRefreshTokenExpirationKey, cookieOptions),
      ]).pipe(
        Effect.catchTags({
          [ErrorTag.CookieRemoveError]: (error) =>
            new SetAuthTokenError({
              message: `Failed to remove token: ${error.message}`,
              cause: error,
            }),
          [ErrorTag.CookieAccessError]: (error) =>
            new SetAuthTokenError({
              message: `Failed to access cookies: ${error.message}`,
              cause: error,
            }),
        })
      );
    }),
  };
});

export const AuthTokenLive = Layer.effect(EngineAuthToken, make);
EngineAuthToken.Live = AuthTokenLive;

export { EngineAuthToken as AuthToken };
